import { useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, CheckCircle2, Loader2, Mail, Paperclip, X } from 'lucide-react';
import { api } from '@/lib/api';
import { correoPorDefecto, estadoCorreo, horaPorDefecto, textoProgramado } from '@/lib/correoLiquidacion';
import { classNames } from '@/lib/utils';
import CuandoEnviar, { Cuando, errorCuando, isoDe } from './CuandoEnviar';

type Estado = 'pendiente' | 'enviando' | 'enviado' | 'programado' | 'error';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Enviar a la vez el correo de varias liquidaciones seleccionadas en el
 * listado. Cada propietario recibe lo mismo que en el paso 6, con el texto y
 * los adjuntos por defecto. Se envían de una en una: así un fallo no para el
 * resto y se ve el resultado de cada una.
 *
 * Programados, salen todos a la misma hora: cada PDF se genera ahora y se
 * guarda con su liquidación.
 */
export default function EnvioMasivo({
  liquidaciones,
  owners,
  generarPdf,
  onLiquidacionActualizada,
  onCerrar,
}: {
  liquidaciones: any[];
  owners: any[];
  /** El mismo PDF que «Descargar», en base64 */
  generarPdf: (liq: any) => Promise<{ nombre: string; base64: string }>;
  onLiquidacionActualizada: (liq: any) => void;
  onCerrar: () => void;
}) {
  const filas = useMemo(() => liquidaciones.map(liq => {
    const owner = owners.find(o => o.id === liq.ownerId);
    const para = String(owner?.email || '').trim();
    const anterior = [...(liq.emailsEnviados || [])].reverse().find((e: any) => e.estado === 'enviado');
    const programado = liq.correoProgramado || null;
    return { liq, owner, para, anterior, programado, correo: correoPorDefecto(liq, owner), sinCorreo: !EMAIL_RE.test(para) };
  }), [liquidaciones, owners]);

  // Por defecto no se repite lo ya enviado o programado ni se intenta lo que no tiene correo
  const [incluidas, setIncluidas] = useState<Set<string>>(
    () => new Set(filas.filter(f => !f.sinCorreo && !f.anterior && !f.programado).map(f => f.liq.id)),
  );
  const [cuando, setCuando] = useState<Cuando>({ modo: 'ahora' });
  const programando = cuando.modo === 'programar';
  const [estados, setEstados] = useState<Record<string, { estado: Estado; detalle?: string }>>({});
  const [enviando, setEnviando] = useState(false);
  const [terminado, setTerminado] = useState(false);

  // Uno que está saliendo ahora mismo no se puede ni repetir ni reprogramar
  const aEnviar = filas.filter(f => incluidas.has(f.liq.id) && !f.sinCorreo && f.programado?.estado !== 'enviando');
  const resumen = Object.values(estados).reduce(
    (acc, e) => ({ ...acc, [e.estado]: (acc[e.estado] || 0) + 1 }),
    {} as Record<Estado, number>,
  );

  function alternar(id: string) {
    setIncluidas(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  async function enviarTodas() {
    const programadoPara = isoDe(cuando) || undefined;
    setEnviando(true);
    // En serie: son pocas, y un error en una no corta las demás
    for (const f of aEnviar) {
      setEstados(prev => ({ ...prev, [f.liq.id]: { estado: 'enviando' } }));
      try {
        const liquidacionPdf = await generarPdf(f.liq);
        const r = await api.enviarEmailLiquidacion(f.liq.id, {
          para: f.para,
          asunto: f.correo.asunto,
          texto: f.correo.texto,
          liquidacionPdf,
          adjuntarFactura: f.correo.adjuntarFactura,
          programadoPara,
        });
        onLiquidacionActualizada(r.liquidacion);
        setEstados(prev => ({ ...prev, [f.liq.id]: { estado: programadoPara ? 'programado' : 'enviado' } }));
      } catch (err: any) {
        setEstados(prev => ({ ...prev, [f.liq.id]: { estado: 'error', detalle: err?.message || 'error' } }));
      }
    }
    setEnviando(false);
    setTerminado(true);
  }

  const fecha = (iso: string) => new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 p-6 pb-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-sereno-dark">Enviar por email</h3>
            <p className="text-sm text-sereno-dark/60 mt-1">
              Cada propietario recibe su liquidación con el texto y los adjuntos de siempre (los mismos que en el paso 6).
              Sale de no-reply@mail.tusereno.es; las respuestas llegan a gestion@tusereno.es.
            </p>
          </div>
          {!enviando && (
            <button onClick={onCerrar} className="text-sereno-dark/40 hover:text-sereno-dark" title="Cerrar"><X size={20} /></button>
          )}
        </div>

        <div className="overflow-y-auto divide-y divide-gray-100">
          {filas.map(f => {
            const st = estados[f.liq.id];
            const incluida = incluidas.has(f.liq.id) && !f.sinCorreo;
            return (
              <label key={f.liq.id} className={classNames('flex items-start gap-3 px-6 py-3', f.sinCorreo ? 'opacity-60' : 'cursor-pointer')}>
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={incluida}
                  disabled={f.sinCorreo || enviando || terminado}
                  onChange={() => alternar(f.liq.id)}
                />
                <div className="flex-1 min-w-0 text-sm">
                  <p className="font-semibold text-sereno-dark truncate">
                    {f.liq.ownerName} <span className="font-normal text-sereno-dark/50">· {f.liq.periodo}</span>
                  </p>
                  <p className="text-sereno-dark/60 truncate">
                    {f.sinCorreo ? <span className="text-red-600">Sin correo válido en su ficha: se omite</span> : f.para}
                  </p>
                  <p className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-sereno-dark/50 mt-0.5">
                    {f.correo.adjuntos.map((a, i) => (
                      <span key={i} className="inline-flex items-center gap-1">
                        <Paperclip size={11} /> {a.texto}
                        {a.aviso && <span className="text-amber-700">({a.aviso})</span>}
                      </span>
                    ))}
                  </p>
                  {f.programado && !st && (
                    <p className={classNames('text-xs mt-0.5', f.programado.estado === 'fallido' ? 'text-red-600' : 'text-blue-700')}>
                      {textoProgramado(f.programado)}{incluida ? (programando ? ': se sustituirá' : ': se cancelará al enviarlo ahora') : ''}
                    </p>
                  )}
                  {f.anterior && !st && (
                    <p className="text-xs text-amber-700 mt-0.5">Ya se envió el {fecha(f.anterior.fecha)} a {f.anterior.para} ({estadoCorreo(f.anterior).texto.toLowerCase()}){incluida ? ': se enviará otra vez' : ''}</p>
                  )}
                  {st?.estado === 'error' && <p className="text-xs text-red-600 mt-0.5">{st.detalle}</p>}
                </div>
                <div className="shrink-0 w-24 text-right text-xs pt-0.5">
                  {st?.estado === 'enviando' && <span className="inline-flex items-center gap-1 text-sereno-dark/60"><Loader2 size={12} className="animate-spin" /> {programando ? 'Programando' : 'Enviando'}</span>}
                  {st?.estado === 'enviado' && <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 size={12} /> Enviado</span>}
                  {st?.estado === 'programado' && <span className="inline-flex items-center gap-1 text-blue-700"><CalendarClock size={12} /> Programado</span>}
                  {st?.estado === 'error' && <span className="inline-flex items-center gap-1 text-red-600"><AlertTriangle size={12} /> Error</span>}
                </div>
              </label>
            );
          })}
        </div>

        {!terminado && (
          <div className="px-6 pt-4 border-t border-gray-100">
            <CuandoEnviar cuando={cuando} onCambiar={setCuando} porDefecto={() => horaPorDefecto()} disabled={enviando} />
          </div>
        )}

        <div className={classNames('flex items-center justify-between gap-3 p-6 pt-4', terminado && 'border-t border-gray-100')}>
          <p className="text-sm text-sereno-dark/60">
            {terminado
              ? [
                  resumen.enviado ? `${resumen.enviado} enviado${resumen.enviado === 1 ? '' : 's'}` : '',
                  resumen.programado ? `${resumen.programado} programado${resumen.programado === 1 ? '' : 's'}` : '',
                  resumen.error ? `${resumen.error} con error` : '',
                ].filter(Boolean).join(' · ') || 'Nada enviado'
              : `${aEnviar.length} de ${filas.length} se ${programando ? 'programarán' : 'enviarán'}`}
          </p>
          {terminado ? (
            <button onClick={onCerrar} className="btn-primary">Cerrar</button>
          ) : (
            <div className="flex items-center gap-3">
              <button onClick={onCerrar} disabled={enviando} className="btn-ghost">Cancelar</button>
              <button onClick={enviarTodas} disabled={enviando || aEnviar.length === 0 || Boolean(errorCuando(cuando))} className="btn-primary flex items-center gap-2">
                {enviando ? <Loader2 className="animate-spin" size={16} /> : programando ? <CalendarClock size={16} /> : <Mail size={16} />}
                {enviando
                  ? (programando ? 'Programando…' : 'Enviando…')
                  : `${programando ? 'Programar' : 'Enviar'} ${aEnviar.length} correo${aEnviar.length === 1 ? '' : 's'}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
